import { useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Alert, AlertDescription, Input, resourceKeys, useDebouncedValue } from '@aimmo/ui';
import type { ContactDuplicate } from '@aimmo/contracts';
import { api, assertOk } from '../lib/api.js';

export interface TaxIdFieldProps {
  kind: 'codiceFiscale' | 'partitaIva';
  value: unknown;
  onChange: (value: string) => void;
  onBlur: () => void;
  disabled: boolean;
  placeholder?: string;
  /** The id the kit's label points at; it belongs on the input, not the wrapper. */
  id?: string;
}

/**
 * A tax id input that warns when the same code is already on file.
 *
 * Deliberately a warning and not a rule (docs/domain/04-contacts.md): the
 * legacy data contains real duplicates, and the person entering a contact is the
 * one who can tell a duplicate from two siblings sharing an address. The API
 * enforces nothing here either — there is no unique index.
 */
export function TaxIdField({
  kind,
  value,
  onChange,
  onBlur,
  disabled,
  placeholder,
  id,
}: TaxIdFieldProps) {
  const text = typeof value === 'string' ? value : '';
  // On the edit screen the contact must not report itself as its own duplicate.
  const params: { id?: string } = useParams({ strict: false });
  const excludeId = params.id;

  // Typing a codice fiscale is sixteen keystrokes; without the delay it is
  // sixteen requests.
  const code = useDebouncedValue(text.trim(), 400);
  const asked = code.length >= 8;
  const found = useQuery({
    queryKey: [...resourceKeys.all('contacts'), 'duplicates', kind, code, excludeId ?? null],
    queryFn: async ({ signal }) =>
      assertOk(
        await api.contacts.duplicates({
          query: { [kind]: code, ...(excludeId ? { excludeId } : {}) },
          fetchOptions: { signal },
        }),
      ).body,
    enabled: asked,
    // The hint is an extra; failing to fetch it must not disturb the form, and
    // asking again will not make it more useful.
    retry: false,
  });
  const duplicates: ContactDuplicate[] = asked ? (found.data ?? []) : [];

  return (
    <div className="grid gap-2">
      <Input
        id={id}
        value={text}
        disabled={disabled}
        placeholder={placeholder}
        autoCapitalize="characters"
        spellCheck={false}
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value.toUpperCase())}
      />
      {duplicates.length > 0 && (
        <Alert tone="warning">
          <AlertDescription>
            Già presente su {duplicates.map((item) => item.displayName).join(', ')}. Verifica che
            non sia un duplicato.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
